import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';

/**
 * Modale générique du kit interne : backdrop, panneau centré, en-tête avec
 * titre et bouton de fermeture, corps et pied projetés.
 *
 * Même motif que idra-confirm-dialog : composant présentationnel, le parent
 * contrôle l'affichage via @if et réagit à (closed). Échap, le clic sur le
 * fond et le bouton × ferment.
 *
 * Usage :
 *  @if (enEdition(); as folder) {
 *    <idra-dialog [header]="'Modifier « ' + folder.name + ' »'" (closed)="enEdition.set(null)">
 *      …formulaire…
 *      <div idra-dialog-footer>
 *        <button idra-button variant="secondary" (click)="enEdition.set(null)">Annuler</button>
 *        <button idra-button (click)="save()">Enregistrer</button>
 *      </div>
 *    </idra-dialog>
 *  }
 */
@Component({
  selector: 'idra-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'closed.emit()',
  },
  styles: `
    .backdrop {
      position: fixed;
      inset: 0;
      z-index: 100;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      background: var(--idra-surface-backdrop);
    }

    .panel {
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 560px;
      max-height: calc(100vh - 48px);
      background: var(--idra-surface-overlay);
      border: 1px solid var(--idra-border);
      border-radius: var(--idra-radius-lg);
      box-shadow: var(--idra-shadow-lg);
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 20px 24px 0;
    }

    .header h3 {
      margin: 0;
      font-size: 17px;
      color: var(--idra-text);
    }

    .close {
      display: inline-flex;
      padding: 6px;
      border: 0;
      border-radius: var(--idra-radius-full);
      background: transparent;
      color: var(--idra-text-muted);
      font-size: 14px;
      cursor: pointer;
    }

    .close:hover {
      background: var(--idra-surface-hover);
      color: var(--idra-text);
    }

    .close:focus-visible {
      outline: none;
      box-shadow: var(--idra-focus-ring);
    }

    .body {
      padding: 14px 24px;
      overflow-y: auto;
    }

    .footer {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      padding: 0 24px 20px;
    }
  `,
  template: `
    <div class="backdrop" (click)="closed.emit()">
      <div
        class="panel"
        role="dialog"
        aria-modal="true"
        [attr.aria-label]="header()"
        (click)="$event.stopPropagation()"
      >
        <div class="header">
          <h3>{{ header() }}</h3>
          <button type="button" class="close" aria-label="Fermer" (click)="closed.emit()">
            <i class="pi pi-times" aria-hidden="true"></i>
          </button>
        </div>
        <div class="body">
          <ng-content />
        </div>
        <div class="footer">
          <ng-content select="[idra-dialog-footer]" />
        </div>
      </div>
    </div>
  `,
})
export class IdraDialog {
  readonly header = input.required<string>();

  /** Fermeture demandée (Échap, backdrop, bouton ×) : le parent retire la modale. */
  readonly closed = output<void>();
}
